import utils from "utils";

export const toControllerLabel = (device) =>
  device?.friendlyName || utils.getModelFromMFVE?.(device?.model) || device?.deviceGuid;

const sortByString = (getValue) => (a, b) => {
  const aVal = (getValue(a) || "").toLowerCase();
  const bVal = (getValue(b) || "").toLowerCase();
  if (aVal < bVal) return -1;
  if (aVal > bVal) return 1;
  return 0;
};

const buildHierarchyContext = ({
  organizations,
  sites,
  gateways,
  includeAllOrganizations,
  activeOrganizationGuid,
}) => {
  const sortByLabel = sortByString((node) => node.label);
  const sortSitesByName = sortByString((site) => site.name);

  const visibleOrganizations = includeAllOrganizations
    ? organizations
    : organizations.filter((org) => org.guid === activeOrganizationGuid);

  const visibleOrgGuids = new Set(visibleOrganizations.map((org) => org.guid));

  const visibleSites = sites.filter((site) =>
    visibleOrgGuids.has(site?.customer?.customerGuid || site?.customerGuid)
  );

  const visibleGateways = gateways.filter((gateway) =>
    visibleOrgGuids.has(gateway?.customer?.customerGuid)
  );

  const organizationByGuid = new Map(visibleOrganizations.map((org) => [org.guid, org]));
  const siteByGuid = new Map(visibleSites.map((site) => [site.siteGuid, site]));

  const siteToOrganizationGuid = new Map();
  for(const site of visibleSites) {
    const orgGuid = site?.customer?.customerGuid || site?.customerGuid;
    if (site?.siteGuid && orgGuid) {
      siteToOrganizationGuid.set(site.siteGuid, orgGuid);
    }
  }

  const gatewaysBySite = new Map();
  const gatewaysByOrganization = new Map();

  for (const gateway of visibleGateways) {
    const orgGuid = gateway?.customer?.customerGuid;
    if (!orgGuid) return;

    if (!gatewaysByOrganization.has(orgGuid)) {
      gatewaysByOrganization.set(orgGuid, []);
    }
    gatewaysByOrganization.get(orgGuid).push(gateway);

    const siteGuid = gateway?.site?.siteGuid || gateway?.siteGuid;
    if (!siteGuid) return;

    if (!gatewaysBySite.has(siteGuid)) {
      gatewaysBySite.set(siteGuid, []);
    }
    gatewaysBySite.get(siteGuid).push(gateway);
  };

  const gatewayByIdentifier = new Map(visibleGateways.map((gateway) => [gateway.macAddress, gateway]));

  const controllerToGatewayMap = new Map();
  for (const gateway of visibleGateways) {
    const gatewayIdentifier = gateway?.macAddress;
    if (!gatewayIdentifier) return;

    for (const device of (gateway.devices || [])) {
      if (device?.deviceGuid) {
        controllerToGatewayMap.set(device.deviceGuid, gatewayIdentifier);
      }
    };
  };

  const buildControllerNodes = (devices) =>
    (devices || [])
      .map((device) => {
        const label = toControllerLabel(device);
        if (!device?.deviceGuid) return null;
        return {
          id: device.deviceGuid,
          label,
          type: "controller",
          selectable: true,
          items: [],
        };
      })
      .filter(Boolean)
      .sort(sortByLabel);

  const buildGatewayNode = (gateway) => {
    const gatewayIdentifier = gateway?.macAddress;
    if (!gatewayIdentifier) return null;

    return {
      id: gatewayIdentifier,
      label: gateway?.name || gatewayIdentifier,
      type: "gateway",
      selectable: true,
      items: buildControllerNodes(gateway?.devices),
    };
  };

  const buildGatewaysForSite = (siteGuid) =>
    (gatewaysBySite.get(siteGuid) || [])
      .map(buildGatewayNode)
      .filter(Boolean)
      .sort(sortByLabel);

  const buildSiteNode = (site) => {
    if (!site?.siteGuid) return null;

    return {
      id: site.siteGuid,
      label: site.name,
      type: "site",
      selectable: true,
      items: buildGatewaysForSite(site.siteGuid),
    };
  };

  const buildUngroupedGatewayNodes = (organizationGuid, siteGatewayIdentifiers) =>
    (gatewaysByOrganization.get(organizationGuid) || [])
      .filter((gateway) => !siteGatewayIdentifiers.has(gateway?.macAddress))
      .map(buildGatewayNode)
      .filter(Boolean)
      .sort(sortByLabel);

  const buildOrganizationNode = (organization) => {
    if (!organization?.guid) return null;

    const organizationSites = visibleSites
      .filter((site) => (site?.customer?.customerGuid || site?.customerGuid) === organization.guid)
      .slice()
      .sort(sortSitesByName);

    const siteNodes = organizationSites.map(buildSiteNode).filter(Boolean);

    const siteGatewayIdentifiers = new Set(
      siteNodes.flatMap((siteNode) => (siteNode.items || []).map((gatewayNode) => gatewayNode.id))
    );

    const ungroupedGateways = buildUngroupedGatewayNodes(
      organization.guid,
      siteGatewayIdentifiers
    );

    return {
      id: organization.guid,
      label: organization.name,
      type: "organization",
      selectable: true,
      items: [...siteNodes, ...ungroupedGateways],
    };
  };

  const hierarchy = visibleOrganizations
    .map(buildOrganizationNode)
    .filter(Boolean)
    .sort(sortByLabel);

  return {
    organizationHierarchyData: hierarchy,
    organizationByGuid,
    siteByGuid,
    siteToOrganizationGuid,
    gatewayByIdentifier,
    controllerGuidToGatewayIdentifier: controllerToGatewayMap,
  };
};

export default buildHierarchyContext;
export { buildHierarchyContext };
